import {
  Box,
  Button,
  captureVideoFrame,
  Dialog,
  DialogPopup,
  DialogTitle,
  Icon,
  Image,
  LiveVideo,
  openCameraStream,
  stopCameraStream,
  Text,
} from "@salestrak/ui";
import { useEffect, useRef, useState } from "react";

import { MediaLightbox } from "#app/components/media/media-lightbox";
import { registerMediaObjectUrl, releaseMediaObjectUrl } from "#app/lib/media-content";
import { countOf } from "#app/lib/utils/pluralize";

type Shot = { file: File; id: string; uri: string };

type CameraProblem = "denied" | "unavailable";

const PROBLEM_COPY: Record<CameraProblem, string> = {
  denied: "Camera access is off. Turn it on in your browser's settings, or add from your library.",
  unavailable: "No camera could be opened here. Add from your library instead.",
};

const SHOT_TILE = 44;

let shotSeq = 0;

function progressOf({
  taken,
  held,
  min,
  cap,
}: {
  taken: number;
  held: number;
  min: number | null;
  cap: number | null;
}): string {
  const total = held + taken;
  const label = cap !== null ? `${total} of ${cap}` : countOf(total, "photo");
  const short = min === null ? 0 : min - total;
  return short > 0 ? `${label} · ${short} more needed` : label;
}

export function CameraCaptureDialog({
  open,
  title,
  held,
  min,
  cap,
  single,
  onDone,
  onUseLibrary,
}: {
  open: boolean;
  title: string;
  held: number;
  min: number | null;
  cap: number | null;
  single: boolean;
  onDone: (files: File[]) => void;
  onUseLibrary: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [problem, setProblem] = useState<CameraProblem | null>(null);
  const [shots, setShots] = useState<Shot[]>([]);
  const [reviewing, setReviewing] = useState<number | null>(null);
  const [flash, setFlash] = useState(false);
  const strip = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);

  // The camera light must go out the moment Done is pressed.
  useEffect(() => {
    if (!open) return;
    let live: MediaStream | null = null;
    let cancelled = false;
    setProblem(null);
    openCameraStream()
      .then((opened) => {
        if (cancelled) return stopCameraStream(opened);
        live = opened;
        setStream(opened);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setProblem(
          error instanceof DOMException && error.name === "NotAllowedError"
            ? "denied"
            : "unavailable",
        );
      });
    return () => {
      cancelled = true;
      stopCameraStream(live);
      setStream(null);
    };
  }, [open]);

  const shotsRef = useRef(shots);
  shotsRef.current = shots;
  useEffect(
    () => () => {
      for (const shot of shotsRef.current) releaseMediaObjectUrl(shot.id);
    },
    [],
  );

  useEffect(() => {
    const el = strip.current;
    if (el) el.scrollTo({ behavior: "smooth", left: el.scrollWidth });
  }, [shots.length]);

  // Two frames: the first can run before the lit frame reaches the screen.
  useEffect(() => {
    if (!flash) return;
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setFlash(false));
    });
    return () => cancelAnimationFrame(frame);
  }, [flash]);

  const full = !single && cap !== null && held + shots.length >= cap;

  const finish = (kept: Shot[]) => {
    for (const shot of kept) releaseMediaObjectUrl(shot.id);
    setShots([]);
    setReviewing(null);
    onDone(kept.map((shot) => shot.file));
  };

  const shoot = async () => {
    const video = videoRef.current;
    if (!video || full) return;
    setFlash(true);
    const frame = frameRef.current?.getBoundingClientRect();
    const file = await captureVideoFrame(video, frame && frame.height > 0 ? frame.width / frame.height : undefined);
    if (!file) return;
    const uri = URL.createObjectURL(file);
    const shot = { file, id: `camera-shot-${shotSeq++}`, uri };
    registerMediaObjectUrl(shot.id, uri, uri);
    if (single) return finish([shot]);
    setShots((current) => [...current, shot]);
  };

  const removeShot = (id: string) => {
    releaseMediaObjectUrl(id);
    setShots((current) => {
      const next = current.filter((shot) => shot.id !== id);
      if (next.length === 0) setReviewing(null);
      return next;
    });
  };

  return (
    <Dialog
      onOpenChange={(next) => {
        if (!next) finish(shots);
      }}
      open={open}
      responsive={false}
    >
      {/* `dark` scopes the dark tokens: a camera reads on black in either
          theme, the way a phone's own does. */}
      <DialogPopup
        className="dark bg-black pt-[env(safe-area-inset-top)]"
        showCloseButton={false}
        size="screen"
      >
        {/* The frame fills this container, its shape held between the
            tallest and widest aspects — measured against the container, so
            a tall phone's frame runs from the top to the shutter instead of
            sitting in black bands. The heading and the strip float over it,
            so it takes the whole width a phone has rather than ceding rows
            to them. */}
        <Box
          align="center"
          className="[container-type:size]"
          fill="flex"
          justify="center"
          minHeight={0}
          overflow="hidden"
        >
          <Box
            align="center"
            className="h-[min(100cqh,calc(min(100cqw,133.333cqh)*16/9))] w-[min(100cqw,133.333cqh)]"
            justify="center"
            overflow="hidden"
            position="relative"
            ref={frameRef}
          >
            {problem ? (
              <Box align="center" direction="column" gap="md" maxWidth={280} padding="lg">
                <Icon color="muted" name="camera" size="lg" />
                <Text align="center" size="sm">
                  {PROBLEM_COPY[problem]}
                </Text>
                <Button
                  onClick={() => {
                    finish(shots);
                    onUseLibrary();
                  }}
                  variant="outline"
                >
                  <Icon name="image" size="md" />
                  Photo library
                </Button>
              </Box>
            ) : (
              <>
                <LiveVideo fill fit="cover" stream={stream} videoRef={videoRef} />
                <Box
                  aria-hidden
                  className={
                    flash
                      ? "bg-black opacity-70"
                      : "bg-black opacity-0 transition-opacity duration-200 ease-out motion-reduce:transition-none"
                  }
                  inset={0}
                  pointerEvents="none"
                  position="absolute"
                />
              </>
            )}
            {/* The heading over the frame's top, on a shade just deep
                enough to read against a bright sky. */}
            <Box
              align="center"
              className="bg-gradient-to-b from-black/60 to-transparent"
              direction="row"
              gap="sm"
              insetX={0}
              justify="between"
              paddingBottom="xl"
              paddingTop="md"
              paddingX="md"
              position="absolute"
              top={0}
            >
              <DialogTitle className="truncate text-sm font-semibold text-white">
                {title}
              </DialogTitle>
              {single ? null : (
                // Tabular figures so the changing count does not shuffle its neighbours.
                <Box
                  className="bg-black/40 backdrop-blur-md"
                  paddingX="sm"
                  paddingY="2xs"
                  rounded="full"
                  shrink={false}
                >
                  <Text className="text-white tabular-nums" size="xs" weight="medium">
                    {full ? "Limit reached" : progressOf({ cap, held, min, taken: shots.length })}
                  </Text>
                </Box>
              )}
            </Box>
            {/* The shots taken, along the frame's foot: newest at the end,
                scrolled into view as it lands. */}
            {shots.length > 0 ? (
              <Box
                align="center"
                bottom={0}
                className="bg-gradient-to-t from-black/60 to-transparent [scrollbar-width:none]"
                direction="row"
                gap="xs"
                insetX={0}
                overflowX="auto"
                paddingBottom="sm"
                paddingTop="xl"
                paddingX="sm"
                position="absolute"
                ref={strip}
              >
                {shots.map((shot, index) => (
                  <Box
                    aria-label={`View photo ${index + 1} of ${shots.length}`}
                    as="button"
                    className="ring-1 ring-white/30 transition-[scale,opacity] duration-300 ease-out starting:scale-75 starting:opacity-0 motion-reduce:transition-none"
                    height={SHOT_TILE}
                    key={shot.id}
                    onClick={() => setReviewing(index)}
                    overflow="hidden"
                    pressFeedback
                    rounded="md"
                    shrink={false}
                    width={SHOT_TILE}
                  >
                    <Image alt="" fill fit="cover" src={shot.uri} />
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>
        </Box>
        {/* Shutter centered, Done balanced against an empty cell, so the
            shutter sits under the thumb whatever the strip holds. */}
        <Box
          align="center"
          className="pb-[max(env(safe-area-inset-bottom),--spacing(5))]"
          columns={3}
          paddingTop="lg"
          paddingX="lg"
        >
          <Box />
          <Box align="center">
            {/* The phone shutter: a white disc inside a white ring. The disc
                sinks on pointer-down, the instant the press lands. */}
            <Box
              align="center"
              aria-label={full ? "Photo limit reached" : "Take photo"}
              as="button"
              className="group size-[72px] rounded-full border-[3px] border-white disabled:opacity-40"
              disabled={!stream || full}
              justify="center"
              onClick={() => void shoot()}
            >
              <Box className="size-[58px] rounded-full bg-white transition-transform duration-100 ease-out group-active:scale-90 motion-reduce:transition-none" />
            </Box>
          </Box>
          <Box align="end">
            {/* Plain text, the way a phone's camera says it: the shutter is
                the one solid control on the screen. */}
            <Button className="text-base font-semibold text-white" onClick={() => finish(shots)} variant="ghost">
              Done
            </Button>
          </Box>
        </Box>
        <MediaLightbox
          initialIndex={reviewing ?? 0}
          media={shots.map((shot) => ({ contentType: "image/*", id: shot.id }))}
          onOpenChange={(next) => {
            if (!next) setReviewing(null);
          }}
          onRemove={removeShot}
          open={reviewing !== null}
          title={title}
        />
      </DialogPopup>
    </Dialog>
  );
}
